import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, Paper, Typography, Button, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { maskAddress, maskPhoneNumber, parseDateAndTime, shouldMaskData, formatCurrency } from '../../helpers/UtilityHelper';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFTextField } from "../../common/hook-form";
import { Link } from 'react-router-dom';
import { CourierFilter, MerchantFilterCached } from '../../common/ProshipFormComponents';
import { formatDate } from '../../common/util';
import { getToken } from '../../helpers/ReduxHelpers';
import { useSelector } from 'react-redux';
import jwt_decode from "jwt-decode";
import StatusChip from '../../common/StatusChip';
import PaymentBadge from '../../common/PaymentBadge';
import { FieldLabel, FilterField } from '../../common/FilterBar';
import { statusBadgeSx, kpiCardSx, kpiRowSx, filterButtonSx, awbLinkSx, accentColor, exportButtonSx } from '../../common/sharedStyles';
import NdrDownloadDialog from './NdrDownloadDialog';
import palette from '../../../theme/palette';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {wordBreak: 'break-word'}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const AwbLink = ({ awb }) => (
    <Box
        component={Link}
        to={`/order-detail/${awb}`}
        sx={awbLinkSx}
    >
        {awb}
    </Box>
);

const NdrStatusBadge = ({ status }) => {
    if (!status) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
    const isOpen = ['INITIATED', 'REQUESTED', 'REPORTED'].includes(status);
    return (
        <Box component="span" sx={{
            ...statusBadgeSx(isOpen ? 'warning' : 'success'),
            textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '10px',
        }}>
            {status}
        </Box>
    );
};

const NdrActionBadge = ({ action }) => {
    if (!action) return <CellLine muted>—</CellLine>;
    const variant = action === 'INITIATE_RTO' ? 'error' : action === 'REATTEMPT' ? 'info' : 'neutral';
    return (
        <Box component="span" sx={{
            ...statusBadgeSx(variant),
            textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: '10px',
        }}>
            {action.replace(/_/g, ' ')}
        </Box>
    );
};

// ─── KPI helpers ──────────────────────────────────────────────────────────────

function computeKPIs(rows) {
    if (!rows || !rows.length) return null;
    const total = rows.length;
    const ages = rows.map(r => r.ndrData?.ndrAge).filter(a => a != null);
    const avgAge = ages.length ? (ages.reduce((s, v) => s + v, 0) / ages.length).toFixed(1) : '—';
    const reattempts = rows.map(r => r.ndrData?.reAttemptCount).filter(a => a != null);
    const avgReattempt = reattempts.length ? (reattempts.reduce((s, v) => s + v, 0) / reattempts.length).toFixed(1) : '—';
    const ofdCounts = rows.map(r => r.ndrData?.ofdCount).filter(a => a != null);
    const avgOfd = ofdCounts.length ? (ofdCounts.reduce((s, v) => s + v, 0) / ofdCounts.length).toFixed(1) : '—';
    const codCount = rows.filter(r => r.payment_mode === 'COD').length;
    const prepaidCount = total - codCount;

    // Top NDR reason
    const reasons = {};
    rows.forEach(r => {
        const reason = r.ndrData?.ndrCpStatusDescription;
        if (reason) reasons[reason] = (reasons[reason] || 0) + 1;
    });
    const topReason = Object.entries(reasons).sort((a, b) => b[1] - a[1])[0];

    return { total, avgAge, avgReattempt, avgOfd, codCount, prepaidCount, topReason };
}

function KPIRow({ rows }) {
    const kpis = useMemo(() => computeKPIs(rows), [rows]);
    if (!kpis) return null;

    const cards = [
        { label: 'Total NDRs', value: kpis.total, color: 'primary' },
        { label: 'Avg NDR Age', value: `${kpis.avgAge} days`, color: 'warning' },
        { label: 'COD / Prepaid', value: `${kpis.codCount} / ${kpis.prepaidCount}`, color: 'info' },
        { label: 'Top Reason', value: kpis.topReason ? kpis.topReason[0] : '—', sub: kpis.topReason ? `${kpis.topReason[1]} orders` : '', color: 'error' },
        { label: 'Avg Reattempts', value: kpis.avgReattempt, color: 'secondary' },
        { label: 'Avg OFD Count', value: kpis.avgOfd, color: 'success' },
    ];

    return (
        <Box sx={kpiRowSx}>
            {cards.map(c => (
                <Paper key={c.label} sx={kpiCardSx(c.color)}>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem' }}>
                        {c.label}
                    </Typography>
                    <Typography variant='h5' sx={{ mt: 0.5, fontWeight: 800, color: accentColor(c.color), fontSize: c.label === 'Top Reason' ? '0.85rem' : '1.25rem' }}>
                        {c.value}
                    </Typography>
                    {c.sub && <Typography variant='caption' sx={{ color: 'text.disabled' }}>{c.sub}</Typography>}
                </Paper>
            ))}
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const NDRDataTable = (props) => {
    const data = props.data;
    const { piiAccessAllow } = jwt_decode(getToken());
    const { report } = useSelector(state => state);

    const columns = [
        {
            field: 'merchantDetail',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <CellLine bold>{row?.merchantDetail?.name || '—'}</CellLine>,
        },
        {
            field: 'orderdetails',
            headerName: 'Order Details', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 200, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.reference || '—'}</CellLine>
                    {row.shippingLabelId && row.reference !== row.shippingLabelId && (
                        <CellLine muted>{row.shippingLabelId}</CellLine>
                    )}
                    <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{formatDate(row.awbRegisteredDate)}</Box>
                </CellStack>
            ),
        },
        {
            field: 'courierDetails',
            headerName: 'Courier & AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1.3,
            renderCell: ({ row }) => (
                <CellStack>
                    {row.awb_number ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <AwbLink awb={row.awb_number} />
                            <CopyIcon text={row.awb_number} />
                        </Box>
                    ) : <CellLine muted>No AWB</CellLine>}
                    {row?.courierDetail?.name && (
                        <CellLine muted ellipsis>{row.courierDetail.name}</CellLine>
                    )}
                    {row?.courierDetail?.prozoId && (
                        <CellLine muted ellipsis>{row.courierDetail.prozoId}</CellLine>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'status',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1,
            renderCell: ({ row }) => <StatusChip status={row.orderStatus} />,
        },
        {
            field: 'ndrstatus',
            headerName: 'NDR Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 130,
            renderCell: ({ row }) => <NdrStatusBadge status={row?.ndrData?.ndrStatus} />,
        },
        {
            field: 'shipment',
            headerName: 'Delivery Address', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 180, flex: 1.5,
            renderCell: ({ row }) => {
                const d = row.delivery_details;
                if (!d) return <CellLine muted>—</CellLine>;
                const masked = shouldMaskData(report, row, piiAccessAllow);
                const addr = `${d.to_address}, ${d.to_city}, ${d.to_state}, ${d.to_pincode}`;
                return (
                    <CellStack>
                        <CellLine bold>{d.to_name || '—'}</CellLine>
                        <CellLine muted>{masked ? maskPhoneNumber(d.to_phone_number) : d.to_phone_number}</CellLine>
                        <Box sx={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {masked ? maskAddress(`${d.to_address}, ${d.to_city}, ${d.to_state}`) : addr}
                        </Box>
                    </CellStack>
                );
            },
        },
        {
            field: 'ndrReason',
            headerName: 'NDR Reason', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine>{row.ndrData?.ndrCpStatusDescription || '—'}</CellLine>
                    {row.ndrData?.ndrReportedDate && (
                        <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{parseDateAndTime(row.ndrData.ndrReportedDate)}</Box>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'ndrcomments',
            headerName: 'NDR Comments', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1.2,
            renderCell: ({ row }) => (
                <CellLine muted>{row.ndrData?.remark || '—'}</CellLine>
            ),
        },
        {
            field: 'ndrAge',
            headerName: 'NDR Age', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 85,
            renderCell: ({ row }) => {
                const age = row.ndrData?.ndrAge;
                if (age == null) return <CellLine muted>—</CellLine>;
                const color = age <= 2 ? palette.success.main : age <= 5 ? palette.warning.dark : palette.error.main;
                return (
                    <Tooltip title={`${age} day${age !== 1 ? 's' : ''} since NDR reported`}>
                        <Box sx={{ fontSize: '13px', fontWeight: 700, color }}>{age}d</Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'reattemptCount',
            headerName: 'Reattempts', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 90,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row.ndrData?.reAttemptCount ?? 0}
                </Box>
            ),
        },
        {
            field: 'attempts',
            headerName: 'OFD Count', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 90,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row.ndrData?.ofdCount ?? 0}
                </Box>
            ),
        },
        {
            field: 'currentAction',
            headerName: 'Current Action', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 130,
            renderCell: ({ row }) => <NdrActionBadge action={row.ndrData?.currentNdrAction} />,
        },
        {
            field: 'paymentDetails',
            headerName: 'Payment', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 110, flex: 0.9,
            renderCell: ({ row }) => (
                <CellStack>
                    {row.invoice_value && (
                        <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                            ₹{row.invoice_value}
                        </Box>
                    )}
                    <PaymentBadge mode={row.payment_mode} />
                </CellStack>
            ),
        },
    ];

    const methods = useForm();
    const { handleSubmit, reset } = methods;

    // ── Full export dialog (merchant + date range, fetches ALL matching rows) ──
    const [downloadOpen, setDownloadOpen] = useState(false);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {props.downloadProps && (
                <NdrDownloadDialog
                    open={downloadOpen}
                    onClose={() => setDownloadOpen(false)}
                    {...props.downloadProps}
                />
            )}
            {/* ── KPI Cards ────────────────────────────────────────────── */}
            <KPIRow rows={data.data} />

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(props.handleFilterSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierFilter filterList={"B2C"} />
                            </FilterField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box
                                    component="button"
                                    type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', ...filterButtonSx('apply') }}
                                >
                                    Filter
                                </Box>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => reset({ state: '', city: '', awb: '', reference: '' })}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', ...filterButtonSx('reset') }}
                                >
                                    Reset
                                </Box>
                            </Box>

                            {/* ── Export (full download: merchant + date range, all matching rows) ── */}
                            {props.downloadProps && (
                                <Box sx={{ ml: 'auto', flex: '0 0 auto' }}>
                                    <Button variant='outlined' size='small' startIcon={<FileDownloadOutlinedIcon />} sx={exportButtonSx}
                                        onClick={() => setDownloadOpen(true)}>
                                        Download
                                    </Button>
                                </Box>
                            )}

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: palette.background.paper, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={data.data ? data.data : []}
                    sizePerPage={props.sizePerPage}
                    setSizePerPage={props.setSizePerPage}
                    setPage={props.setPage}
                    page={props.page}
                    pagination={true}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    getRowId={(row) => row.id}
                />
            </Box>
        </Box>
    );
};
